import dynamic from 'next/dynamic'
import { createContext, FC, useCallback, useContext, useState } from 'react'

import { NeedSettingPersonalUserSnackbar } from '@/components/organisms/membership/NeedSettingPersonalUserSnackbar'
import { needSettingPersonalUserSnackbarRef } from '@/components/organisms/membership/NeedSettingPersonalUserSnackbar/useNeedSettingPersonalUserSnackbar'
import { config } from '@/config'
import { User } from '@/core'
import { handleChunkLoadError } from '@/lib/errorHandler'

type Options = {
  showSnackbarWhenClose?: boolean
  isRegisterNewMembership?: boolean
}

type SettingUserInfoModalContext = {
  isOpen: boolean
  showModal: (options?: Options) => void
}

const Context = createContext({} as SettingUserInfoModalContext)

export function useSettingUserInfoModal() {
  return useContext(Context)
}

type SettingUserInfoModalProviderProps = {
  user: User | null
}

export const SettingUserInfoModalProvider: FC<SettingUserInfoModalProviderProps> =
  ({ user, children }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [showOptions, setShowOptions] = useState<Options | undefined>()

    const showModal = useCallback((options?: Options) => {
      setOpen(true)
      setShowOptions(options)
    }, [])

    return (
      <Context.Provider value={{ showModal, isOpen: open }}>
        {children}
        <MembershipUserInfoModal
          open={open}
          onClose={() => {
            setOpen(false)
            if (showOptions?.showSnackbarWhenClose) {
              needSettingPersonalUserSnackbarRef.current?.triggerShowMessage(
                showOptions?.isRegisterNewMembership
              )
            }
          }}
          onCompleted={() => setOpen(false)}
        />
        {user && <NeedSettingPersonalUserSnackbar user={user} />}
      </Context.Provider>
    )
  }

const MembershipUserInfoModal = dynamic<{
  open: boolean
  onClose: () => void
  onCompleted: () => void
}>(
  config.modules.membership
    ? import('@/components/organisms/membership/MembershipUserInfo')
        .then(m => m.MembershipUserInfoModal)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  {
    ssr: false,
  }
)
